import { useState, useEffect, useCallback } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'
import { TrendingUp, Calendar, Users, ArrowUpDown } from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'
import { formatISTDate } from '../../../components/lib/dateUtils'
import { AdminPanelButton } from './AdminPanelButton'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions
} from 'chart.js'
import { Line } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

interface DashboardStats {
  totalUsers: number
  activeUsers: number
  totalChats: number
  totalMessages: number
  totalDeletedMessages: number
  totalSuggestions: number
  todayUsers: number
  todayChats: number
  todayMessages: number
}

interface TopUser {
  userId: string
  username: string
  email: string
  firstName?: string
  lastName?: string
  chatCount: number
  messageCount: number
  lastActive?: string
}

interface AnalyticsData {
  userGrowth: Array<{ date: string; count: number }>
  chatActivity: Array<{ date: string; count: number }>
  messageActivity: Array<{ date: string; count: number }>
  topUsers: TopUser[]
}

function getDisplayName(user: TopUser): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return name || user.username
}

type SortOption = 'chatCount' | 'messageCount' | 'recent'

export function AnalyticsDashboard({ stats }: { stats: DashboardStats }) {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    userGrowth: [],
    chatActivity: [],
    messageActivity: [],
    topUsers: []
  })
  const [loading, setLoading] = useState(false)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d')
  const [sortBy, setSortBy] = useState<SortOption>('recent')

  const fetchAnalytics = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}admin/analytics?range=${timeRange}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        setAnalytics(data)
      }
    } catch (error) {
      console.error('Error fetching analytics:', error)
    } finally {
      setLoading(false)
    }
  }, [timeRange])

  useEffect(() => {
    fetchAnalytics()
  }, [fetchAnalytics])

  // Sort top users based on selected criteria
  const sortedTopUsers = [...analytics.topUsers].sort((a, b) => {
    switch (sortBy) {
      case 'chatCount':
        return b.chatCount - a.chatCount
      case 'messageCount':
        return b.messageCount - a.messageCount
      case 'recent': {
        // For recent activity, use the actual last active timestamp
        if (a.lastActive && b.lastActive) {
          return new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime()
        } else if (a.lastActive && !b.lastActive) {
          return -1 // a has activity, b doesn't
        } else if (!a.lastActive && b.lastActive) {
          return 1 // b has activity, a doesn't
        } else {
          // Both have no activity, fall back to chat count
          return b.chatCount - a.chatCount
        }
      }
      default:
        return 0
    }
  })

  // Prepare chart data
  const chartData = {
    labels: analytics.userGrowth.map(item => 
      new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    ),
    datasets: [
      {
        label: 'Active Users',
        data: analytics.userGrowth.map(item => item.count),
        borderColor: '#1B2A4A',
        backgroundColor: 'rgba(27, 42, 74, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: '#1B2A4A',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: '#1B2A4A',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 3,
      },
      {
        label: 'Chats',
        data: analytics.chatActivity.map(item => item.count),
        borderColor: '#C9A84C',
        backgroundColor: 'rgba(201, 168, 76, 0.1)',
        fill: true,
        tension: 0.4,
        borderDash: [5, 5],
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: '#C9A84C',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: '#C9A84C',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 3,
      },
    ],
  }

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 13,
            weight: 500,
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              const unit = label.includes('Active Users') ? 'users' : 'chats';
              label += context.parsed.y + ' ' + unit;
            }
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11,
          },
          callback: function(value) {
            return value;
          }
        },
      },
    },
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--white)',
    border: '1px solid var(--bd)',
    borderRadius: 12,
    boxShadow: 'var(--sh-sm)',
    padding: 20,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Time Range Selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Calendar size={20} style={{ color: 'var(--muted)' }} />
        <div style={{ display: 'flex', gap: 8 }}>
          {(['7d', '30d', '90d'] as const).map((range) => (
            <AdminPanelButton
              key={range}
              type="button"
              variant={timeRange === range ? 'active' : 'default'}
              onClick={() => setTimeRange(range)}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </AdminPanelButton>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div style={cardStyle}>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 500, color: 'var(--text)', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <TrendingUp size={20} style={{ color: 'var(--navy)' }} />
          User Activity Over Time
        </h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 16px' }}>Total users and active users trend</p>
        {loading ? (
          <div style={{ height: 384, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 32, height: 32, border: '2px solid var(--bd)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
          </div>
        ) : analytics.userGrowth.length > 0 ? (
          <div style={{ height: 384 }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        ) : (
          <div style={{ height: 384, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 14 }}>
            No data available
          </div>
        )}
      </div>

      {/* Top Users */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
          <div>
            <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 500, color: 'var(--text)', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={20} style={{ color: 'var(--gold)' }} />
              Most Active Users
              {sortBy === 'recent' && (
                <span style={{ fontSize: 10, fontWeight: 600, background: 'var(--gold-lt)', border: '1px solid var(--gold-bd)', color: 'var(--gold)', padding: '2px 8px', borderRadius: 100 }}>
                  Recent Activity
                </span>
              )}
            </h3>
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
              {sortBy === 'recent' ? 'Users sorted by most recent chat activity' : 'Users with the most chat activity'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ArrowUpDown size={16} style={{ color: 'var(--muted)' }} />
            <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
              <SelectTrigger className="w-40" style={{ border: '1px solid var(--bd)', borderRadius: 9 }}>
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="chatCount">Chat Count</SelectItem>
                <SelectItem value="messageCount">Message Count</SelectItem>
                <SelectItem value="recent">Recent Activity</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {loading ? (
          <div style={{ height: 192, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 32, height: 32, border: '2px solid var(--bd)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
          </div>
        ) : sortedTopUsers.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sortedTopUsers.map((user, index) => (
              <div
                key={user.userId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 16,
                  background: 'var(--bg2)',
                  border: '1px solid var(--bd)',
                  borderRadius: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: 'var(--navy)',
                      color: 'var(--white)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {index + 1}
                  </div>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', margin: 0 }}>
                      {getDisplayName(user)}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0 0' }}>{user.email}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 24, alignItems: 'center', textAlign: 'right' }}>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>Last Active</div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>
                      {user.lastActive ? formatISTDate(user.lastActive) : 'Never'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>Chats</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{user.chatCount}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>Messages</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{user.messageCount}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ height: 192, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 14 }}>
            No user activity data available
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginBottom: 8 }}>Average Messages per Chat</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy)' }}>
            {stats.totalChats > 0 ? (stats.totalMessages / stats.totalChats).toFixed(1) : '0'}
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginBottom: 8 }}>User Engagement Rate</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--gold)' }}>
            {stats.totalUsers > 0 ? ((stats.activeUsers / stats.totalUsers) * 100).toFixed(1) : '0'}%
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginBottom: 8 }}>Regenerated Messages</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>{stats.totalDeletedMessages}</div>
        </div>
      </div>
    </div>
  )
}

